import { useEffect, useMemo, useState } from 'react';
import { BiPlus, BiSearch } from 'react-icons/bi';
import { FaClockRotateLeft } from 'react-icons/fa6';
import { useSearchParams } from 'react-router-dom';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { DateNavPicker } from '@/components/DateNavPicker';
import { Input } from '@/components/Inputs';
import { Tile } from '@/components/Tile/Tile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import {
  useCreateDigitalNavigationLogMutation,
  useDigitalNavigationLogByDateQuery,
  useDigitalNavigationLogPdfMutation,
  useUpdateDigitalNavigationLogMutation,
} from '@/store/digital-navigation-log/digital-navigation-log.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { getBrowserTimeZone } from '@/utils/timezone';
import { toast } from '@/utils/toast';

import { LOG_VIEW_PARAM, useLogView } from '../useLogView';

import { NavEntriesSection } from './components/NavEntriesSection';
import { NavFooterSection } from './components/NavFooterSection';
import { NavHeaderFields } from './components/NavHeaderFields';
import { NavMainFields } from './components/NavMainFields';
// import { NavSignaturesSection } from './components/NavSignaturesSection';
import { useNavigationLogForm } from './hooks/useNavigationLogForm';
import {
  formStateToCreateValues,
  formStateToUpdateValues,
  navigationLogToFormState,
} from './mappers';
import s from './NavigationLog.module.scss';
import NavigationLogHistory from './NavigationLogHistory/NavigationLogHistory';
import type { NavigationEntry } from './types';

const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

const isoToParam = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const paramToISO = (param: string | null): string => {
  if (param && DATE_PARAM_RE.test(param)) {
    const d = new Date(`${param}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const NavigationLog = () => {
  const isMobile = useIsMobile();
  const { volumeUnit } = useUnitPreferences();
  const vessel = useVessels((s) => s.vessel);
  const { view, openHistory, openedFromLogsCard, startNewEntry } = useLogView();
  const [historySearch, setHistorySearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const isoDate = useMemo(() => paramToISO(dateParam), [dateParam]);
  const dateKey = useMemo(() => isoToParam(isoDate), [isoDate]);

  const { state, logId, setField, setState, setLogId, prefillForm, resetForm } =
    useNavigationLogForm(isoDate);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const { data: fetchedLog, isFetching } = useDigitalNavigationLogByDateQuery(dateKey);
  const createMutation = useCreateDigitalNavigationLogMutation();
  const updateMutation = useUpdateDigitalNavigationLogMutation();
  const pdfMutation = useDigitalNavigationLogPdfMutation();

  useEffect(() => {
    if (fetchedLog) {
      prefillForm(navigationLogToFormState(fetchedLog, volumeUnit), fetchedLog.id);
    } else {
      resetForm(isoDate);
    }
  }, [fetchedLog, isoDate, prefillForm, resetForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToDate = (iso: string) => {
    const param = isoToParam(iso);
    const today = isoToParam(new Date().toISOString());
    setSearchParams(
      (prev) => {
        if (!param || param === today) prev.delete('date');
        else prev.set('date', param);
        // A picked day is always shown as its page, and this has to be one
        // navigation: a second `setSearchParams` in the same tick would start
        // from the old params and drop the date again.
        prev.delete(LOG_VIEW_PARAM);
        return prev;
      },
      { replace: true },
    );
  };

  const handleMainFieldChange = (field: 'date' | 'passageFrom' | 'passageTo', value: string) => {
    if (field === 'date') {
      navigateToDate(value);
    } else {
      setField(field, value);
    }
  };

  const handleStartAdd = () => {
    setEditingId(null);
    setAddingNew(true);
  };

  const handleStartEdit = (id: string) => {
    setAddingNew(false);
    setEditingId(id);
  };

  const handleCancel = () => {
    setAddingNew(false);
    setEditingId(null);
  };

  const handleSaveEdit = (id: string, patch: Partial<NavigationEntry>) => {
    setState((prev) => ({
      ...prev,
      entries: prev.entries.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
    setEditingId(null);
  };

  const handleAddNew = (values: Omit<NavigationEntry, 'id'>) => {
    setState((prev) => ({ ...prev, entries: [...prev.entries, { id: newId(), ...values }] }));
    setAddingNew(false);
  };

  const handleRemove = (id: string) =>
    setState((prev) => ({ ...prev, entries: prev.entries.filter((e) => e.id !== id) }));

  const handleFuelChange = (index: 0 | 1, value: string) =>
    setState((prev) => {
      const next: [string, string] = [...prev.fuelTakenOn];
      next[index] = value;
      return { ...prev, fuelTakenOn: next };
    });

  const handleWaterChange = (index: 0 | 1, value: string) =>
    setState((prev) => {
      const next: [string, string] = [...prev.waterTakenOn];
      next[index] = value;
      return { ...prev, waterTakenOn: next };
    });

  const handleSave = async () => {
    try {
      if (logId) {
        const saved = await updateMutation.mutateAsync(
          formStateToUpdateValues(logId, state, volumeUnit),
        );
        setLogId(saved.id);
      } else {
        const created = await createMutation.mutateAsync(
          formStateToCreateValues(state, volumeUnit),
        );
        setLogId(created.id);
      }
      toast.success('Navigation log saved');
    } catch {
      toast.error('Couldn’t save the navigation log. Please try again.');
    }
  };

  const handleExport = async () => {
    if (!logId) return;
    const blob = await pdfMutation.mutateAsync({ id: logId, timezone: getBrowserTimeZone() });
    await downloadBlob(blob, `navigation-log-${dateKey}.pdf`);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isExporting = pdfMutation.isPending;

  const shipName = vessel?.vessel_name ?? '';
  const registrationNumber = vessel?.registration_number ?? '';
  const onImoNumber = vessel?.structure_number_value ?? '';

  return (
    <>
      <div className={s.toolbar}>
        <div className={s.toolbarControls}>
          {view === 'form' && (
            <ButtonOutlinedSmall onClick={openHistory}>
              <FaClockRotateLeft />
              History
            </ButtonOutlinedSmall>
          )}
          {!isMobile && view === 'form' && (
            <DateNavPicker value={state.date} onChange={navigateToDate} />
          )}
          {view === 'form' && (
            <div className={s.toolbarActions}>
              <ButtonOutlinedSmall
                onClick={() => void handleExport()}
                disabled={isExporting || !logId}
              >
                {isExporting ? 'Exporting…' : 'Export'}
              </ButtonOutlinedSmall>
              <ButtonSmall onClick={() => void handleSave()} disabled={isSaving || isFetching}>
                {isSaving ? 'Saving…' : 'Save'}
              </ButtonSmall>
            </div>
          )}
        </div>
        {view === 'history' && (
          <Input
            placeholder="Search"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            startIcon={<BiSearch size={18} />}
            className={s.searchInput}
          />
        )}
        {openedFromLogsCard && (
          <ButtonSmall onClick={startNewEntry} aria-label="Add Entry">
            <BiPlus size={20} aria-hidden="true" />
          </ButtonSmall>
        )}
      </div>

      {isMobile && view === 'form' && (
        <DateNavPicker
          value={state.date}
          onChange={navigateToDate}
          className={s.mobileDatePicker}
        />
      )}

      {view === 'history' && (
        <NavigationLogHistory search={historySearch} onSelectLog={navigateToDate} />
      )}

      {view === 'form' && (
        <div className={s.content}>
          <div className={s.sectionsList}>
            <Tile>
              <NavHeaderFields
                shipName={shipName}
                registrationNumber={registrationNumber}
                onImoNumber={onImoNumber}
              />
            </Tile>

            <Tile>
              <NavMainFields
                date={state.date}
                passageFrom={state.passageFrom}
                passageTo={state.passageTo}
                onChange={handleMainFieldChange}
              />
            </Tile>

            <NavEntriesSection
              entries={state.entries}
              editingId={editingId}
              addingNew={addingNew}
              onStartAdd={handleStartAdd}
              onStartEdit={handleStartEdit}
              onCancel={handleCancel}
              onSaveEdit={handleSaveEdit}
              onAddNew={handleAddNew}
              onRemove={handleRemove}
            />

            <Tile>
              <NavFooterSection
                maintenance={state.maintenance}
                fuelTakenOn={state.fuelTakenOn}
                waterTakenOn={state.waterTakenOn}
                onChange={(field, value) => setField(field, value)}
                onFuelChange={handleFuelChange}
                onWaterChange={handleWaterChange}
              />
            </Tile>

            {/* Signatures hidden for now: the navigation-log API has no signatures support
                (no /digital-navigation-log/{id}/sign endpoint, no signature DTO fields). */}
            {/* <Tile>
              <NavSignaturesSection />
            </Tile> */}
          </div>
        </div>
      )}
    </>
  );
};

export default NavigationLog;
